/**
 * Замена `flutter_screenutil`.
 *
 * В приложении `ScreenUtilInit` получает `designSize` 1366×1010 в альбомной
 * ориентации и 1010×1366 в книжной (`AppSettingsWrapper`), после чего размеры
 * пишутся как `40.w` и `152.h`:
 *
 *   .w → value * screenWidth  / designWidth
 *   .h → value * screenHeight / designHeight
 *
 * Размеры шрифтов в `AppStyles` идут без `.sp` — значит, они не
 * масштабируются вовсе и остаются в логических пикселях. В браузере
 * логический пиксель Flutter = CSS-пиксель, поэтому `fontSize: 64`
 * переносится как `64px` без пересчёта.
 *
 * CSS-переменные `--sw`/`--sh` держат цену одной единицы дизайна в CSS-пикселях,
 * так что в стилях можно писать `calc(var(--sw) * 40)`, а в коде — `w(40)`.
 */

export const LANDSCAPE_DESIGN_SIZE = { width: 1366, height: 1010 } as const;
export const PORTRAIT_DESIGN_SIZE = { width: 1010, height: 1366 } as const;

export interface ScreenMetrics {
  /** Ширина окна в CSS-пикселях. */
  readonly width: number;
  /** Высота окна в CSS-пикселях. */
  readonly height: number;
  /** `context.isLandscape` — `screenWidth > screenHeight`. */
  readonly isLandscape: boolean;
  /** Цена единицы `.w`. */
  readonly sw: number;
  /** Цена единицы `.h`. */
  readonly sh: number;
}

export function designSizeFor(isLandscape: boolean): { width: number; height: number } {
  return isLandscape ? LANDSCAPE_DESIGN_SIZE : PORTRAIT_DESIGN_SIZE;
}

export function computeMetrics(width: number, height: number): ScreenMetrics {
  const isLandscape = width > height;
  const design = designSizeFor(isLandscape);
  return {
    width,
    height,
    isLandscape,
    sw: width / design.width,
    sh: height / design.height,
  };
}

/** Метрики до первого замера: базовый дизайн один к одному. */
const INITIAL_METRICS = computeMetrics(LANDSCAPE_DESIGN_SIZE.width, LANDSCAPE_DESIGN_SIZE.height);

let current: ScreenMetrics = INITIAL_METRICS;

/**
 * Записаны ли метрики в документ хотя бы раз.
 *
 * Без этого флага первый замер, совпавший с базовым дизайном (а это ровно
 * разрешение киоска, 1366×1010), уходил в ранний выход — и `--sw`, `--sh`
 * и `data-orientation` в документе не появлялись вовсе. Правила вида
 * `:root[data-orientation='landscape'] .grid` тогда молча не применялись.
 */
let applied = false;

const listeners = new Set<() => void>();

export function getMetrics(): ScreenMetrics {
  return current;
}

/** `40.w` */
export function w(value: number): number {
  return value * current.sw;
}

/** `152.h` */
export function h(value: number): number {
  return value * current.sh;
}

/** `40.w` в виде строки для инлайновых стилей: `'calc(var(--sw) * 40)'`. */
export function wv(value: number): string {
  return `calc(var(--sw) * ${value})`;
}

/** `152.h` в виде строки для инлайновых стилей. */
export function hv(value: number): string {
  return `calc(var(--sh) * ${value})`;
}

/** `40.w` в виде `'123.45px'` — когда `calc()` использовать нельзя. */
export function wpx(value: number): string {
  return `${w(value)}px`;
}

/** `152.h` в виде `'123.45px'`. */
export function hpx(value: number): string {
  return `${h(value)}px`;
}

function applyToDocument(metrics: ScreenMetrics): void {
  const root = document.documentElement;
  root.style.setProperty('--sw', `${metrics.sw}px`);
  root.style.setProperty('--sh', `${metrics.sh}px`);
  root.style.setProperty('--screen-width', `${metrics.width}px`);
  root.style.setProperty('--screen-height', `${metrics.height}px`);
  root.dataset.orientation = metrics.isLandscape ? 'landscape' : 'portrait';
}

export function setMetrics(width: number, height: number): ScreenMetrics {
  const next = computeMetrics(width, height);
  const unchanged =
    next.width === current.width &&
    next.height === current.height &&
    next.isLandscape === current.isLandscape;
  // Первый замер пишется в документ всегда, даже если он в точности равен
  // базовому дизайну: иначе переменных и ориентации в DOM не будет.
  if (unchanged && applied) {
    return current;
  }
  current = next;
  applied = true;
  applyToDocument(next);
  if (unchanged) return current;
  for (const listener of listeners) listener();
  return next;
}

export function subscribeToMetrics(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Подписывает метрики на размер окна. Возвращает функцию отписки.
 *
 * `visualViewport` точнее `innerWidth`: он не считает полосу прокрутки и
 * учитывает экранную клавиатуру — на планшете-киоске она перекрывает экран
 * ровно так же, как `MediaQuery.viewInsets` во Flutter.
 */
export function installScreenMetrics(): () => void {
  const read = (): void => {
    const viewport = window.visualViewport;
    setMetrics(
      viewport?.width ?? window.innerWidth,
      viewport?.height ?? window.innerHeight,
    );
  };

  read();

  const viewport = window.visualViewport;
  window.addEventListener('resize', read);
  window.addEventListener('orientationchange', read);
  viewport?.addEventListener('resize', read);

  return () => {
    window.removeEventListener('resize', read);
    window.removeEventListener('orientationchange', read);
    viewport?.removeEventListener('resize', read);
  };
}

/** Только для тестов: вернуть метрики к базовому дизайну. */
export function resetMetricsForTest(): void {
  current = INITIAL_METRICS;
  applied = false;
}
